import { useCallback, useMemo } from "react";
import { useRouter } from "next/router";
import { useProduct, useUI } from "../contexts";
import Listings from "./legacy-listings";
import { Footer } from "./footer";
import { useStoreListingApi } from "./useStoreListingApi";
import LoadMoreSection from "./search-listing/LoadMoreSection";
import BackToTopButton from "./search-listing/BackToTopButton";
import { useLoadAhead } from "./search-listing/useLoadAhead";
import { usePagedList } from "./search-listing/usePagedList";
import { useScrollRestoration } from "../utils/useScrollRestoration";
import GridSidebarAd from "./search-listing/GridSidebarAd";
import { SellwildSellerTv } from "./embed-tags/sellwild-tv";
import { trackError } from "../utils/error-tracking";
import { asError } from "../utils/error-message";
import type { Product } from '../utils/product/product-types';

const StoreListing = ({ id }: { id: string | number }) => {
  const { setProduct, currentList, replaceCurrentList } = useProduct();
  const { clientSide } = useUI();
  const { fetchOtherListings, pickDataForListing } = useStoreListingApi();
  const { asPath } = useRouter();
  useScrollRestoration();

  // Page 1 (and Back's restore) is put in the product context by
  // seller-shared; the shared pager takes the pages after it and keeps the
  // list in the same place, so product-page flips walk this seller's list.
  const fetchPage = useCallback(async (page: number) => {
    const { listings } = await fetchOtherListings(id, null, page);
    // Narrowed to the UI's Product view; see contexts/ProductProvider for the
    // same boundary between the search row and Product.
    const items = pickDataForListing(listings, 32) as unknown as Product[];
    return { items, hasMore: items.length > 0 };
  }, [id, fetchOtherListings, pickDataForListing]);

  const store = useMemo(() => ({
    list: currentList || [],
    setList: (list: Product[], page: number) => replaceCurrentList?.(list, { page }),
  }), [currentList, replaceCurrentList]);

  // Keyed by the URL: seller-shared recalls Back's list by the same key.
  const pager = usePagedList<Product>({
    listKey: asPath,
    fetchPage,
    enabled: !!id,
    store,
    onError: (error) => trackError(asError(error)),
  });
  const { loading, done } = pager;

  // The next page loads ahead like every other grid page.
  const { ref, loadMore: loadAhead } = useLoadAhead({
    onLoadMore: pager.loadNext,
    loading,
    done,
    shownCount: store.list.length,
  });

  return (
    <div className="search-listings">
      <div>
        <div className="listing-cont">
          <Listings
            listName="currentList"
            pageType="store"
            list={currentList}
            setProduct={setProduct}
            max={99999}
            clientSide={clientSide}
          />
          <LoadMoreSection done={done} loading={loading} onLoadMore={loadAhead} refCallback={ref} />
          <BackToTopButton />
        </div>
        {id && (
          <div className="seller-tv-wrapper">
            <SellwildSellerTv userId={id as string} />
          </div>
        )}
        <Footer />
      </div>
      <GridSidebarAd />
    </div>
  );
};

export default StoreListing;
